import { matchRoutes } from 'react-router-dom';
import type { AuthSession } from './types';

/**
 * ADR-0067 decision 6 + decision 9 (FE side): the ONE frontend checker for "may this session
 * open this screen". The sidebar and the router guard ask this module; nothing else reads
 * `AuthSession.allowedWindows`, `route-windows.json` or `window-policies.json`.
 *
 * <p>Presence, not level (decision 2, PB `f_phfc_window_security`): a route opens when ANY of
 * its PB windows is in the session's `allowedWindows` (PB `dw_phfc_window_security` result, run
 * once at login), or when the hand-written policy file declares that window `menu-open` (a
 * window with no `window_modules` row that PB's menu never disables — decision 4). Window keys
 * are compared EXACTLY as stored — `w_x (mode)` and `w_x(mode)` both exist in PB's data and are
 * never normalised (decision 3).
 *
 * <p>The catalog (the two generated json maps) is injected by the app layer
 * ({@code app/nav.ts} `APP_SCREEN_ACCESS`) — `src/shared/` never imports `src/app/` — and tests
 * build their own with small fixture maps.
 */

/** PB's refusal (`f_open_window` / `f_phfc_open_window_std.srf`), shown in enforce mode only. */
export const WINDOW_ACCESS_REFUSAL = 'You do not have security access to this window.';

export type WindowGateMode = 'log' | 'enforce';

/** One row of `window-policies.json` (hand-written, owned by the orchestrator). */
export interface WindowPolicy {
  window: string;
  policy: 'menu-open' | 'deny';
  reason?: string | undefined;
  cite?: string | undefined;
}

export interface WindowCatalog {
  /** `route-windows.json`: route pattern exactly as written in `router.tsx` → PB window keys. */
  routeWindows: Readonly<Record<string, readonly string[]>>;
  /** `window-policies.json`. */
  policies: readonly WindowPolicy[];
}

export type RouteAccessReason =
  /** a window of the route is in `allowedWindows` */
  | 'granted'
  /** a window of the route is declared `menu-open` in the policy file */
  | 'menu-open'
  /** the route has no window in the map — not a gated screen (home, testers hub, …) */
  | 'unmapped'
  /** the session predates `allowedWindows` (persisted by an older build): allow, never log */
  | 'unknown-session'
  /** denied: no window granted; the reason vocabulary matches the BE would-403 line */
  | 'not-granted'
  | 'no-row'
  | 'no-act'
  | 'child';

export interface RouteAccess {
  allowed: boolean;
  /** The window the verdict is about: the granting one when allowed, the first one otherwise. */
  window: string | null;
  reason: RouteAccessReason;
}

export interface ScreenAccess {
  /** PB windows that gate a route pattern (empty when the route is not in the map). */
  windowsForRoute: (routePattern: string) => readonly string[];
  /**
   * The map key a concrete URL (pathname, optionally with `?search`) resolves to. A query-keyed
   * mode route (`/labels/print-std?mode=license`, lane B decision D6) wins first when all of its
   * params are present with the same values; otherwise react-router's ranking over the bare
   * path keys (static beats `:param`). The bare key of a query-keyed route holds the union.
   */
  matchRoutePattern: (url: string) => string | null;
  mayOpenRoute: (session: AuthSession | null | undefined, routePattern: string) => RouteAccess;
}

/** A session persisted by an older build has no mode: treat it as log-only. */
export function windowGateModeOf(session: AuthSession | null | undefined): WindowGateMode {
  return session?.windowGateMode === 'enforce' ? 'enforce' : 'log';
}

const DENY_REASONS: ReadonlySet<string> = new Set(['no-row', 'no-act', 'child']);

/**
 * True when the session carries a LOADED grant list. Missing (older persisted build) and `null`
 * (server could not load grants) both mean "unknown"; `[]` is loaded — the user has no windows.
 */
export function hasLoadedGrants(session: AuthSession | null | undefined): boolean {
  return Array.isArray(session?.allowedWindows);
}

export function createScreenAccess(catalog: WindowCatalog): ScreenAccess {
  const policyByWindow = new Map<string, WindowPolicy>();
  for (const p of catalog.policies) policyByWindow.set(p.window, p);
  const routeObjects = Object.keys(catalog.routeWindows)
    .filter((key) => !key.includes('?'))
    .map((path) => ({ path }));
  // Query-keyed routes, grouped by path, most-specific (most params) first.
  const queryKeys = new Map<string, Array<{ key: string; params: Array<[string, string]> }>>();
  for (const key of Object.keys(catalog.routeWindows)) {
    const qi = key.indexOf('?');
    if (qi < 0) continue;
    const path = key.slice(0, qi);
    const list = queryKeys.get(path) ?? [];
    list.push({ key, params: [...new URLSearchParams(key.slice(qi + 1)).entries()] });
    queryKeys.set(path, list);
  }
  for (const list of queryKeys.values()) list.sort((a, b) => b.params.length - a.params.length);
  // allowedWindows arrays are replaced (never mutated) by the store, so one Set per array.
  const grantSets = new WeakMap<readonly string[], ReadonlySet<string>>();
  const grantsOf = (allowed: readonly string[]): ReadonlySet<string> => {
    let s = grantSets.get(allowed);
    if (!s) {
      s = new Set(allowed);
      grantSets.set(allowed, s);
    }
    return s;
  };

  const windowsForRoute = (routePattern: string): readonly string[] =>
    Object.prototype.hasOwnProperty.call(catalog.routeWindows, routePattern)
      ? (catalog.routeWindows[routePattern] ?? [])
      : [];

  const matchRoutePattern = (url: string): string | null => {
    const noHash = url.split('#')[0] ?? url;
    const qi = noHash.indexOf('?');
    const bare = qi < 0 ? noHash : noHash.slice(0, qi);
    const candidates = qi < 0 ? undefined : queryKeys.get(bare);
    if (candidates) {
      const search = new URLSearchParams(noHash.slice(qi + 1));
      const hit = candidates.find((c) => c.params.every(([k, v]) => search.get(k) === v));
      if (hit) return hit.key;
    }
    if (routeObjects.length === 0) return null;
    const matches = matchRoutes(routeObjects, bare);
    const last = matches?.[matches.length - 1];
    return last?.route.path ?? null;
  };

  const mayOpenRoute = (
    session: AuthSession | null | undefined,
    routePattern: string,
  ): RouteAccess => {
    const windows = windowsForRoute(routePattern);
    if (windows.length === 0) return { allowed: true, window: null, reason: 'unmapped' };
    const allowedWindows = session?.allowedWindows;
    if (!allowedWindows || !Array.isArray(allowedWindows)) return { allowed: true, window: windows[0] ?? null, reason: 'unknown-session' };
    const grants = grantsOf(allowedWindows);
    // Any-of: the route passes if ANY of its windows passes.
    for (const w of windows) if (grants.has(w)) return { allowed: true, window: w, reason: 'granted' };
    for (const w of windows) {
      if (policyByWindow.get(w)?.policy === 'menu-open') {
        return { allowed: true, window: w, reason: 'menu-open' };
      }
    }
    const first = windows[0] ?? null;
    const declared = first ? policyByWindow.get(first) : undefined;
    const reason: RouteAccessReason =
      declared?.policy === 'deny' && declared.reason && DENY_REASONS.has(declared.reason)
        ? (declared.reason as RouteAccessReason)
        : 'not-granted';
    return { allowed: false, window: first, reason };
  };

  return { windowsForRoute, matchRoutePattern, mayOpenRoute };
}

// ── log-only sink ────────────────────────────────────────────────────────────────────────

const reported = new Set<string>();

/**
 * Log-only (ADR-0067 decision 8): tell the server a screen WOULD have been refused, so FE-only
 * refusals land in the same daily `WINDOW-GATE would-403` table as the backend's.
 *
 * <p>Once per (session, route); fire-and-forget; every failure swallowed; never awaited. Raw
 * `fetch` on purpose (precedent: `reportClientError`): going through `api()` would count this
 * POST as a data write (`dataWrites.ts` marks every cached list stale on a successful non-GET)
 * — a visible behaviour change tonight — and a 401 here must never clear the session.
 */
export function reportWouldForbid(session: AuthSession, route: string, access: RouteAccess): void {
  try {
    const key = `${session.sessionId}|${route}`;
    if (reported.has(key)) return;
    reported.add(key);
    console.info(
      `WINDOW-GATE would-403 window=${access.window ?? '-'} route=${route} reason=${access.reason}`,
    );
    void fetch('/api/security/would-403', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(session.token ? { Authorization: 'Bearer ' + session.token } : {}),
      },
      body: JSON.stringify({ window: access.window, route, reason: access.reason }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* reporting must never break the page */
  }
}

/** Test seam: forget which (session, route) pairs were already reported. */
export function resetWouldForbidReportsForTests(): void {
  reported.clear();
}
